import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { ProtectedRoute } from "@/core/auth/components/ProtectedRoute";
import { P } from "@/core/auth/permissionRegistry";
import { Loader2 } from "lucide-react";
import { isFeatureEnabled } from "@/core/config/featureFlags";

// Core pages (loaded immediately)
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import NotFound from "@/pages/NotFound";

// ─── Lazy-loaded Pages ───────────────────────────────────────────────

// Dashboard & Core
const Dashboard = lazy(() => import("@/pages/Dashboard"));

// Campaigns
const Campaigns = lazy(() => import("@/pages/Campaigns"));
const CampaignCreate = lazy(() => import("@/pages/CampaignCreate"));
const CampaignDetail = lazy(() => import("@/pages/CampaignDetail"));
const CampaignAnalytics = lazy(() => import("@/pages/CampaignAnalytics"));
const AudienceDetail = lazy(() => import("@/pages/AudienceDetail"));
const RecipientDetail = lazy(() => import("@/pages/RecipientDetail"));

// Mail & Designers
const Mail = lazy(() => import("@/pages/Mail"));
const NewMailDesigner = lazy(() => import("@/pages/NewMailDesigner"));
const NewLandingPageDesigner = lazy(() => import("@/pages/NewLandingPageDesigner"));
const AILandingPageDesigner = lazy(() => import("@/pages/AILandingPageDesigner"));
const NewEmailDesigner = lazy(() => import("@/pages/NewEmailDesigner"));
const LandingPages = lazy(() => import("@/pages/LandingPages"));

// Gift Cards
const GiftCardManager = lazy(() => import("@/pages/GiftCardManager"));
const ClientGiftCards = lazy(() => import("@/pages/ClientGiftCards"));
const AgencyGiftCards = lazy(() => import("@/pages/AgencyGiftCards"));
const AdminGiftCards = lazy(() => import("@/pages/AdminGiftCards"));
const AdminGiftCardMarketplace = lazy(() => import("@/pages/AdminGiftCardMarketplace"));
const AdminGiftCardBrands = lazy(() => import("@/pages/AdminGiftCardBrands"));
const PoolDetail = lazy(() => import("@/pages/PoolDetail"));
const PurchaseGiftCard = lazy(() => import("@/pages/PurchaseGiftCard"));
const RecordPurchase = lazy(() => import("@/pages/RecordPurchase"));
const EditPoolPricing = lazy(() => import("@/pages/EditPoolPricing"));
const GiftCardReveal = lazy(() => import("@/pages/GiftCardReveal"));
const EmbedGiftCard = lazy(() => import("@/pages/EmbedGiftCard"));
const PublicRedemption = lazy(() => import("@/pages/PublicRedemption"));
const TestRedemption = lazy(() => import("@/pages/TestRedemption"));

// Contacts
const Contacts = lazy(() => import("@/pages/Contacts"));
const ContactDetail = lazy(() => import("@/pages/ContactDetail"));
const ContactLists = lazy(() => import("@/pages/ContactLists"));
const ListDetail = lazy(() => import("@/pages/contacts/ListDetail"));
const ContactImport = lazy(() => import("@/pages/ContactImport"));

// Call Center
const CallCenterRedemption = lazy(() => import("@/pages/CallCenterRedemption"));
const CallCenterScripts = lazy(() => import("@/pages/CallCenterScripts"));

// Marketing
const MarketingHub = lazy(() => import("@/pages/marketing/MarketingHub"));
const Broadcasts = lazy(() => import("@/pages/marketing/Broadcasts"));
const BroadcastCreate = lazy(() => import("@/pages/marketing/BroadcastCreate"));
const BroadcastDetail = lazy(() => import("@/pages/marketing/BroadcastDetail"));
const Automations = lazy(() => import("@/pages/marketing/Automations"));
const AutomationCreate = lazy(() => import("@/pages/marketing/AutomationCreate"));
const AutomationDetail = lazy(() => import("@/pages/marketing/AutomationDetail"));
const ContentLibrary = lazy(() => import("@/pages/marketing/ContentLibrary"));

// Forms
const Forms = lazy(() => import("@/pages/Forms"));
const FormBuilder = lazy(() => import("@/pages/FormBuilder"));
const FormPublic = lazy(() => import("@/pages/FormPublic"));
const FormAnalytics = lazy(() => import("@/pages/FormAnalytics"));
const FormsDocumentation = lazy(() => import("@/pages/FormsDocumentation"));
const DigitalCampaignLanding = lazy(() => import("@/pages/DigitalCampaignLanding"));
const PURLLandingPage = lazy(() => import("@/pages/PURLLandingPage"));

// Billing
const ClientBillingDashboard = lazy(() => import("@/pages/ClientBillingDashboard"));
const AdminBillingDashboard = lazy(() => import("@/pages/AdminBillingDashboard"));
const AgencyBillingDashboard = lazy(() => import("@/pages/AgencyBillingDashboard"));

// Admin & Settings
const Settings = lazy(() => import("@/pages/Settings"));
const UserManagement = lazy(() => import("@/pages/UserManagement"));
const AgencyManagement = lazy(() => import("@/pages/AgencyManagement"));
const AdminOrganizationManagement = lazy(() => import("@/pages/AdminOrganizationManagement"));
const AdminMessagingTest = lazy(() => import("@/pages/AdminMessagingTest"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const Monitoring = lazy(() => import("@/pages/Monitoring"));
const APIDocumentation = lazy(() => import("@/pages/APIDocumentation"));

// Documentation & Legal
const Documentation = lazy(() => import("@/pages/Documentation"));
const DocEditorPage = lazy(() => import("@/pages/DocEditorPage"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PublicLandingPage = lazy(() => import("@/pages/PublicLandingPage"));

// Microsites — A2P 10DLC compliant client landing pages
const BigBearLanding = lazy(() =>
  import("@/features/microsites/bigbear").then((m) => ({ default: m.BigBearLanding }))
);
const BigBearTerms = lazy(() =>
  import("@/features/microsites/bigbear").then((m) => ({ default: m.BigBearTerms }))
);
const BigBearSmsTerms = lazy(() =>
  import("@/features/microsites/bigbear").then((m) => ({ default: m.BigBearSmsTerms }))
);
const BigBearPrivacy = lazy(() =>
  import("@/features/microsites/bigbear").then((m) => ({ default: m.BigBearPrivacy }))
);
const BigBearCookies = lazy(() =>
  import("@/features/microsites/bigbear").then((m) => ({ default: m.BigBearCookies }))
);

// ─── Loading Fallback ────────────────────────────────────────────────

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const LegacyDocumentationRedirect = () => {
  const { category, slug } = useParams<{ category?: string; slug?: string }>();

  if (!category || !slug) {
    return <Navigate to="/docs" replace />;
  }

  return <Navigate to={`/docs/${category}/${slug}`} replace />;
};

// ─── Microsite host detection ────────────────────────────────────────
// Subdomains like bigbear.mobul.com get a dedicated route tree mounted at
// the root path. This keeps each client microsite isolated from the main
// Mobul app while sharing the same SPA build.
const isBigBearHost =
  typeof window !== "undefined" &&
  /^bigbear\./i.test(window.location.hostname);

// ─── Route Definitions ───────────────────────────────────────────────

export const AppRoutes = () => {
  if (isBigBearHost) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<BigBearLanding />} />
          <Route path="/terms" element={<BigBearTerms />} />
          <Route path="/sms-terms" element={<BigBearSmsTerms />} />
          <Route path="/privacy" element={<BigBearPrivacy />} />
          <Route path="/cookies" element={<BigBearCookies />} />
          {/* The embedded gift-card form lives on the same origin so the
              customer never leaves bigbear.mobul.com (better UX + cleaner
              A2P 10DLC review story). */}
          <Route path="/forms/:formId" element={<FormPublic />} />
          <Route path="/f/:formSlug" element={<FormPublic />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
  <Suspense fallback={<LoadingFallback />}>
    <Routes>
      {/* ── Microsite subpath fallbacks (work without DNS) ────── */}
      <Route path="/bigbear" element={<BigBearLanding />} />
      <Route path="/bigbear/terms" element={<BigBearTerms />} />
      <Route path="/bigbear/sms-terms" element={<BigBearSmsTerms />} />
      <Route path="/bigbear/privacy" element={<BigBearPrivacy />} />
      <Route path="/bigbear/cookies" element={<BigBearCookies />} />

      {/* ── Public Routes ─────────────────────────────────────── */}
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/c/:campaignId/:token" element={<PURLLandingPage />} />
      <Route path="/r/:slug" element={<DigitalCampaignLanding />} />
      <Route path="/f/:formSlug" element={<FormPublic />} />
      <Route path="/forms/:formId" element={<FormPublic />} />
      <Route path="/embed/gift-card" element={<EmbedGiftCard />} />
      <Route path="/embed/gift-card/:campaignId" element={<EmbedGiftCard />} />
      <Route path="/redeem/:campaignId/:redemptionToken" element={<GiftCardReveal />} />
      <Route path="/redeem-gift-card" element={<PublicRedemption />} />
      <Route path="/test-redemption" element={<TestRedemption />} />
      <Route path="/:clientSlug/p/:pageSlug" element={<PublicLandingPage />} />

      {/* ── Legacy Redirects ──────────────────────────────────── */}
      <Route path="/analytics" element={<Navigate to="/monitoring?tab=overview" replace />} />
      <Route path="/performance" element={<Navigate to="/monitoring?tab=performance" replace />} />
      <Route path="/errors" element={<Navigate to="/monitoring?tab=errors" replace />} />
      <Route path="/alerts" element={<Navigate to="/monitoring?tab=alerts" replace />} />
      <Route path="/monitoring/performance" element={<Navigate to="/monitoring?tab=performance" replace />} />
      <Route path="/monitoring/errors" element={<Navigate to="/monitoring?tab=errors" replace />} />
      <Route path="/monitoring/alerts" element={<Navigate to="/monitoring?tab=alerts" replace />} />
      <Route path="/api" element={<Navigate to="/admin/integrations?tab=api" replace />} />
      <Route path="/api-documentation" element={<Navigate to="/api-docs" replace />} />
      <Route path="/zapier" element={<Navigate to="/admin/integrations?tab=zapier" replace />} />
      <Route path="/zapier-templates" element={<Navigate to="/admin/integrations?tab=zapier" replace />} />
      <Route path="/help" element={<Navigate to="/docs" replace />} />
      <Route path="/help/:category/:slug" element={<LegacyDocumentationRedirect />} />
      <Route path="/platform" element={<Navigate to="/" replace />} />
      <Route path="/templates" element={<Navigate to={isFeatureEnabled('mail_library') ? "/mail" : "/"} replace />} />
      <Route path="/credits-billing" element={<Navigate to="/client/billing" replace />} />
      <Route path="/activity" element={<Navigate to="/monitoring?tab=activity" replace />} />
      <Route path="/call-center/logs" element={<Navigate to="/monitoring?tab=redemptions" replace />} />
      <Route path="/admin/system-health" element={<Navigate to="/monitoring?tab=system" replace />} />
      <Route path="/admin/demo-data-generator" element={<Navigate to="/" replace />} />
      <Route path="/admin/financial-reports" element={<Navigate to="/admin/gift-cards-dashboard" replace />} />
      <Route path="/admin/audit-log" element={<Navigate to="/monitoring?tab=activity" replace />} />
      <Route path="/admin/error-logs" element={<Navigate to="/monitoring?tab=errors" replace />} />
      <Route path="/admin/site-directory" element={<Navigate to="/" replace />} />
      <Route path="/admin/demo-data" element={<Navigate to="/" replace />} />
      <Route path="/admin/docs/manage" element={<Navigate to="/docs?tab=manage" replace />} />
      <Route path="/admin/docs" element={<Navigate to="/docs" replace />} />
      <Route path="/admin/docs/:category/:slug" element={<LegacyDocumentationRedirect />} />
      <Route path="/landing-pages/create" element={<Navigate to={isFeatureEnabled('landing_pages') ? "/landing-pages/new" : "/"} replace />} />
      <Route path="/landing-pages/:id/editor" element={<Navigate to={isFeatureEnabled('landing_pages') ? "/landing-pages/:id" : "/"} replace />} />
      <Route path="/landing-pages/:id/canvas" element={<Navigate to={isFeatureEnabled('landing_pages') ? "/landing-pages/:id" : "/"} replace />} />
      <Route path="/ace-forms" element={<Navigate to="/forms" replace />} />
      <Route path="/ace-forms/new" element={<Navigate to="/forms/new" replace />} />
      <Route path="/ace-forms/:formId/builder" element={<Navigate to="/forms/:formId/builder" replace />} />
      <Route path="/ace-forms/:formId/analytics" element={<Navigate to="/forms/:formId/analytics" replace />} />
      <Route path="/ace-forms/docs" element={<Navigate to="/forms/docs" replace />} />
      <Route path="/marketing/campaigns" element={<Navigate to="/marketing/broadcasts" replace />} />
      <Route path="/marketing/campaigns/new" element={<Navigate to="/marketing/broadcasts/new" replace />} />
      <Route path="/marketing/campaigns/:id" element={<Navigate to="/marketing/broadcasts/:id" replace />} />
      <Route path="/marketing/campaigns/:id/edit" element={<Navigate to="/marketing/broadcasts/:id/edit" replace />} />
      <Route path="/marketing/analytics" element={<Navigate to="/marketing" replace />} />
      <Route path="/users" element={<Navigate to="/admin/users" replace />} />
      <Route path="/user-management" element={<Navigate to="/admin/users" replace />} />
      <Route path="/call-scripts" element={<Navigate to="/call-center/scripts" replace />} />
      <Route path="/operations" element={<Navigate to="/gift-cards" replace />} />
      <Route path="/audience" element={<Navigate to="/contacts" replace />} />
      <Route path="/audience/*" element={<Navigate to="/contacts" replace />} />
      <Route path="/mail-library" element={<Navigate to="/mail" replace />} />
      <Route path="/marketing/content-library" element={<Navigate to="/marketing/content" replace />} />
      <Route path="/integrations" element={<Navigate to="/admin/integrations" replace />} />

      {/* ── Dashboard ─────────────────────────────────────────── */}
      <Route path="/" element={<ProtectedRoute permission={P.DASHBOARD_VIEW}><Index /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute permission={P.DASHBOARD_VIEW}><Dashboard /></ProtectedRoute>} />

      {/* ── Campaigns ─────────────────────────────────────────── */}
      <Route path="/campaigns" element={<ProtectedRoute permission={P.CAMPAIGNS_VIEW}><Campaigns /></ProtectedRoute>} />
      <Route path="/campaigns/new" element={<ProtectedRoute permission={P.CAMPAIGNS_CREATE}><CampaignCreate /></ProtectedRoute>} />
      <Route path="/campaigns/:id/edit" element={<ProtectedRoute permission={P.CAMPAIGNS_EDIT}><CampaignCreate /></ProtectedRoute>} />
      <Route path="/campaigns/:id" element={<ProtectedRoute permission={P.CAMPAIGNS_VIEW}><CampaignDetail /></ProtectedRoute>} />
      <Route path="/audiences/:id" element={<ProtectedRoute permission={P.AUDIENCES_VIEW}><AudienceDetail /></ProtectedRoute>} />
      <Route path="/recipients/:id" element={<ProtectedRoute permission={P.CONTACTS_VIEW}><RecipientDetail /></ProtectedRoute>} />
      <Route path="/analytics/campaigns/:id" element={<ProtectedRoute permission={P.CAMPAIGNS_ANALYTICS}><CampaignAnalytics /></ProtectedRoute>} />

      {/* ── Mail & Designers ──────────────────────────────────── */}
      {/* Mail Library routes - gated by mail_library feature flag */}
      <Route path="/mail" element={isFeatureEnabled('mail_library') ? <ProtectedRoute permission={P.TEMPLATES_VIEW}><Mail /></ProtectedRoute> : <Navigate to="/" replace />} />
      <Route path="/mail-designer/:id" element={isFeatureEnabled('mail_library') ? <ProtectedRoute permission={P.TEMPLATES_EDIT}><NewMailDesigner /></ProtectedRoute> : <Navigate to="/" replace />} />
      <Route path="/new-mail-designer/:id" element={isFeatureEnabled('mail_library') ? <ProtectedRoute permission={P.TEMPLATES_EDIT}><NewMailDesigner /></ProtectedRoute> : <Navigate to="/" replace />} />
      <Route path="/template-builder/:id" element={isFeatureEnabled('mail_library') ? <ProtectedRoute permission={P.TEMPLATES_EDIT}><NewMailDesigner /></ProtectedRoute> : <Navigate to="/" replace />} />
      {/* Landing designer route - gated by landing_pages feature flag */}
      <Route path="/new-landing-designer/:id" element={isFeatureEnabled('landing_pages') ? <ProtectedRoute permission={P.LANDING_PAGES_EDIT}><NewLandingPageDesigner /></ProtectedRoute> : <Navigate to="/" replace />} />
      <Route path="/new-email-designer/:id" element={<ProtectedRoute permission={P.TEMPLATES_EDIT}><NewEmailDesigner /></ProtectedRoute>} />
      <Route path="/email-designer/:id" element={<ProtectedRoute permission={P.TEMPLATES_EDIT}><NewEmailDesigner /></ProtectedRoute>} />

      {/* ── Landing Pages - gated by landing_pages feature flag ─── */}
      <Route path="/landing-pages" element={isFeatureEnabled('landing_pages') ? <ProtectedRoute permission={P.LANDING_PAGES_VIEW}><LandingPages /></ProtectedRoute> : <Navigate to="/" replace />} />
      <Route path="/landing-pages/new" element={isFeatureEnabled('landing_pages') ? <ProtectedRoute permission={P.LANDING_PAGES_CREATE}><NewLandingPageDesigner /></ProtectedRoute> : <Navigate to="/" replace />} />
      <Route path="/landing-pages/:id" element={isFeatureEnabled('landing_pages') ? <ProtectedRoute permission={P.LANDING_PAGES_EDIT}><NewLandingPageDesigner /></ProtectedRoute> : <Navigate to="/" replace />} />
      <Route path="/landing-pages-ai/new" element={isFeatureEnabled('landing_pages') ? <ProtectedRoute permission={P.LANDING_PAGES_CREATE}><AILandingPageDesigner /></ProtectedRoute> : <Navigate to="/" replace />} />
      <Route path="/landing-pages-ai/:id" element={isFeatureEnabled('landing_pages') ? <ProtectedRoute permission={P.LANDING_PAGES_EDIT}><AILandingPageDesigner /></ProtectedRoute> : <Navigate to="/" replace />} />

      {/* ── Gift Cards ────────────────────────────────────────── */}
      <Route path="/gift-cards" element={<ProtectedRoute permission={P.GIFT_CARDS_VIEW}><GiftCardManager /></ProtectedRoute>} />
      <Route path="/gift-cards/manager" element={<ProtectedRoute permission={P.GIFT_CARDS_VIEW}><GiftCardManager /></ProtectedRoute>} />
      <Route path="/gift-cards/pools/:poolId" element={<ProtectedRoute permission={P.GIFT_CARDS_MANAGE_POOLS}><PoolDetail /></ProtectedRoute>} />
      <Route path="/gift-cards/purchase/:poolId" element={<ProtectedRoute permission={P.GIFT_CARDS_PURCHASE}><PurchaseGiftCard /></ProtectedRoute>} />
      <Route path="/gift-cards/marketplace" element={<ProtectedRoute permission={P.GIFT_CARDS_MARKETPLACE_ADMIN}><AdminGiftCardMarketplace /></ProtectedRoute>} />
      <Route path="/client/gift-cards" element={<ProtectedRoute permission={P.GIFT_CARDS_VIEW}><ClientGiftCards /></ProtectedRoute>} />
      <Route path="/agency/gift-cards" element={<ProtectedRoute permission={P.GIFT_CARDS_MANAGE_POOLS}><AgencyGiftCards /></ProtectedRoute>} />
      <Route path="/admin/gift-cards-dashboard" element={<ProtectedRoute permission={P.GIFT_CARDS_MARKETPLACE_ADMIN}><AdminGiftCards /></ProtectedRoute>} />
      <Route path="/admin/gift-card-marketplace" element={<ProtectedRoute permission={P.GIFT_CARDS_MARKETPLACE_ADMIN}><AdminGiftCardMarketplace /></ProtectedRoute>} />
      <Route path="/admin/gift-cards" element={<ProtectedRoute permission={P.GIFT_CARDS_BRAND_MANAGEMENT}><AdminGiftCardBrands /></ProtectedRoute>} />
      <Route path="/admin/gift-cards/record-purchase" element={<ProtectedRoute permission={P.GIFT_CARDS_RECORD_PURCHASE}><RecordPurchase /></ProtectedRoute>} />
      <Route path="/admin/gift-cards/pools/:poolId/pricing" element={<ProtectedRoute permission={P.GIFT_CARDS_PRICING}><EditPoolPricing /></ProtectedRoute>} />

      {/* ── Billing ───────────────────────────────────────────── */}
      <Route path="/client/billing" element={<ProtectedRoute permission={P.BILLING_VIEW}><ClientBillingDashboard /></ProtectedRoute>} />
      <Route path="/admin/billing" element={<ProtectedRoute permission={P.BILLING_ADMIN}><AdminBillingDashboard /></ProtectedRoute>} />
      <Route path="/agency/billing" element={<ProtectedRoute permission={P.BILLING_AGENCY_VIEW}><AgencyBillingDashboard /></ProtectedRoute>} />

      {/* ── Contacts ──────────────────────────────────────────── */}
      <Route path="/contacts" element={<ProtectedRoute permission={P.CONTACTS_VIEW}><Contacts /></ProtectedRoute>} />
      <Route path="/contacts/:id" element={<ProtectedRoute permission={P.CONTACTS_VIEW}><ContactDetail /></ProtectedRoute>} />
      <Route path="/contacts/lists" element={<ProtectedRoute permission={P.CONTACTS_VIEW}><ContactLists /></ProtectedRoute>} />
      <Route path="/contacts/lists/:id" element={<ProtectedRoute permission={P.CONTACTS_VIEW}><ListDetail /></ProtectedRoute>} />
      <Route path="/contacts/import" element={<ProtectedRoute permission={P.CONTACTS_IMPORT}><ContactImport /></ProtectedRoute>} />

      {/* ── Call Center ───────────────────────────────────────── */}
      <Route path="/call-center" element={<ProtectedRoute permission={P.CALL_CENTER_REDEEM}><CallCenterRedemption /></ProtectedRoute>} />
      <Route path="/call-center/scripts" element={<ProtectedRoute permission={P.CALL_CENTER_SCRIPTS}><CallCenterScripts /></ProtectedRoute>} />

      {/* ── Marketing ─────────────────────────────────────────── */}
      <Route path="/marketing" element={<ProtectedRoute permission={P.CAMPAIGNS_VIEW}><MarketingHub /></ProtectedRoute>} />
      <Route path="/marketing/broadcasts" element={<ProtectedRoute permission={P.CAMPAIGNS_VIEW}><Broadcasts /></ProtectedRoute>} />
      <Route path="/marketing/broadcasts/new" element={<ProtectedRoute permission={P.CAMPAIGNS_CREATE}><BroadcastCreate /></ProtectedRoute>} />
      <Route path="/marketing/broadcasts/:id" element={<ProtectedRoute permission={P.CAMPAIGNS_VIEW}><BroadcastDetail /></ProtectedRoute>} />
      <Route path="/marketing/broadcasts/:id/edit" element={<ProtectedRoute permission={P.CAMPAIGNS_EDIT}><BroadcastCreate /></ProtectedRoute>} />
      <Route path="/marketing/automations" element={<ProtectedRoute permission={P.CAMPAIGNS_VIEW}><Automations /></ProtectedRoute>} />
      <Route path="/marketing/automations/new" element={<ProtectedRoute permission={P.CAMPAIGNS_CREATE}><AutomationCreate /></ProtectedRoute>} />
      <Route path="/marketing/automations/:id" element={<ProtectedRoute permission={P.CAMPAIGNS_VIEW}><AutomationDetail /></ProtectedRoute>} />
      <Route path="/marketing/automations/:id/edit" element={<ProtectedRoute permission={P.CAMPAIGNS_EDIT}><AutomationCreate /></ProtectedRoute>} />
      <Route path="/marketing/content" element={<ProtectedRoute permission={P.TEMPLATES_VIEW}><ContentLibrary /></ProtectedRoute>} />

      {/* ── Forms ─────────────────────────────────────────────── */}
      <Route path="/forms" element={<ProtectedRoute permission={P.FORMS_VIEW}><Forms /></ProtectedRoute>} />
      <Route path="/forms/docs" element={<ProtectedRoute permission={P.DOCS_VIEW}><FormsDocumentation /></ProtectedRoute>} />
      <Route path="/forms/guide" element={<Navigate to="/forms/docs" replace />} />
      <Route path="/forms/new" element={<ProtectedRoute permission={P.FORMS_CREATE}><FormBuilder /></ProtectedRoute>} />
      <Route path="/forms/:formId/builder" element={<ProtectedRoute permission={P.FORMS_EDIT}><FormBuilder /></ProtectedRoute>} />
      <Route path="/forms/:formId/analytics" element={<ProtectedRoute permission={P.FORMS_ANALYTICS}><FormAnalytics /></ProtectedRoute>} />

      {/* ── Documentation ─────────────────────────────────────── */}
      <Route path="/docs" element={<ProtectedRoute permission={P.DOCS_VIEW}><Documentation /></ProtectedRoute>} />
      <Route path="/docs/:category" element={<ProtectedRoute permission={P.DOCS_VIEW}><Documentation /></ProtectedRoute>} />
      <Route path="/docs/:category/:slug" element={<ProtectedRoute permission={P.DOCS_VIEW}><Documentation /></ProtectedRoute>} />
      <Route path="/docs/:category/:slug/edit" element={<ProtectedRoute permission={P.DOCS_MANAGE}><DocEditorPage /></ProtectedRoute>} />

      {/* ── Settings & Admin ──────────────────────────────────── */}
      <Route path="/settings" element={<ProtectedRoute permission={P.SETTINGS_GENERAL}><Settings /></ProtectedRoute>} />
      <Route path="/settings/:tab" element={<ProtectedRoute permission={P.SETTINGS_GENERAL}><Settings /></ProtectedRoute>} />
      <Route path="/api-docs" element={<ProtectedRoute permission={P.API_VIEW}><APIDocumentation /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute requiredRoles={['admin', 'tech_support']} requiredPermission={P.USERS_MANAGE}><UserManagement /></ProtectedRoute>} />
      <Route path="/agencies" element={<ProtectedRoute permission={P.AGENCIES_VIEW}><AgencyManagement /></ProtectedRoute>} />
      <Route path="/agency-management" element={<ProtectedRoute permission={P.AGENCIES_MANAGE}><AgencyManagement /></ProtectedRoute>} />
      <Route path="/admin/integrations" element={<ProtectedRoute permission={P.INTEGRATIONS_VIEW}><Integrations /></ProtectedRoute>} />
      <Route path="/admin/organizations" element={<ProtectedRoute permission={P.ADMIN_ORGANIZATIONS}><AdminOrganizationManagement /></ProtectedRoute>} />
      <Route path="/admin/messaging-test" element={<ProtectedRoute permission={P.ADMIN_SYSTEM_HEALTH}><AdminMessagingTest /></ProtectedRoute>} />
      <Route path="/monitoring" element={<ProtectedRoute permission={P.MONITORING_VIEW}><Monitoring /></ProtectedRoute>} />

      {/* ── 404 ───────────────────────────────────────────────── */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
  );
};
